import has from '@dojo/framework/core/has';
import { createICacheMiddleware } from '@dojo/framework/core/middleware/icache';
import { theme } from '@dojo/framework/core/middleware/theme';
import { create, v, w } from '@dojo/framework/core/vdom';
import { AudioPlayer, audio } from '@holmescorp/audio';
import { Icon } from '@holmescorp/base-widgets';
import { Keys } from '@holmescorp/common';
import { createNlsMiddleware, keymap, lccstate, load_topic, pathmap, quizmap, readingTopicQueueChange, userstate } from '@holmescorp/lcc-state';
import { RelatedTopics } from '@holmescorp/related-topics';
import css from '../css/topicgroup.m.css';
import topicPageCss from '../css/topicpage.m.css';
import { handleReadingCompleted, handleReadingLoaded } from '../support/xapi';
const nls = createNlsMiddleware('ditabook');
const settings = createNlsMiddleware('settings');
const icache = createICacheMiddleware();
const factory = create({ icache, theme, nls, lccstate, userstate, settings, audio }).properties();
export const TopicSingle = factory(({ properties, middleware: { icache, theme, nls, lccstate, userstate, settings, audio } }) => {
    const { isPlaceholder, messages: { reading = '[ditabook] reading', quiz: quizText = '', return_to_quiz = '[ditabook] return_to_quiz' } } = nls();
    if (isPlaceholder) {
        return null;
    }
    const { messages: { hasRelatedTopics = undefined, association = '[settings] association' } } = settings();
    const { get, path } = lccstate;
    const { get: uget, path: upath } = userstate;
    const bookversion = get(path('bookversion'));
    const lang = window.hcstatic.portal_language || 'en';
    const { title, rel = '' } = properties();
    const themeCss = theme.classes(css);
    const pageCss = theme.classes(topicPageCss);
    const readingTool = uget(upath('toolstate', 'reading'));
    const status = uget(upath('status'));
    const { previous, next, parent = '' } = status[rel];
    const readingQueue = readingTool.topic_queues[parent];
    const isUsingQueue = readingQueue != null && !readingQueue.complete;
    const nextTopic = () => {
        let nextSelectedTopic = next;
        let nextPosition;
        if (isUsingQueue) {
            const position = readingQueue.cursor;
            nextPosition = position + 1;
            nextSelectedTopic = readingQueue.topics && readingQueue.topics[nextPosition];
        }
        if (nextSelectedTopic == null) {
            icache.set('lastTopic', true);
            if (isUsingQueue) {
                const { next: nextTopicGroup, parent: nextParent } = status[parent];
                if (nextTopicGroup) {
                    const { path: tgpath, title } = keymap.get(nextTopicGroup);
                    return { title, path: tgpath };
                }
                else {
                    const { path: tgpath } = keymap.get(nextParent);
                    return { title: quizText, path: `${tgpath}/quiz` };
                }
            }
            const { path: tgpath } = keymap.get(parent);
            const [, explore, , tgf] = tgpath && tgpath.split('/');
            const path = `/${explore}`;
            const { title } = keymap.get(tgf);
            const { quiz } = quizmap.get(parent) || {};
            if (quiz) {
                return {
                    title: quizText,
                    path: `${tgpath}/quiz`
                };
            }
            else {
                return {
                    title,
                    path
                };
            }
        }
        icache.set('lastTopic', false);
        const { title, path } = keymap.get(nextSelectedTopic) || { title: '', path: '' };
        return { title, path, position: nextPosition };
    };
    const previousTopic = () => {
        if (isUsingQueue) {
            const { quiz } = quizmap.get(parent) || {};
            const { path: tgpath } = keymap.get(parent);
            if (quiz) {
                return { title: return_to_quiz, path: `${tgpath}/quiz` };
            }
        }
        if (previous == null) {
            return {
                title: '',
                path: null
            };
        }
        const { title, path } = keymap.get(previous);
        return { title, path };
    };
    const next_prev = (keyval) => {
        const next = nextTopic();
        const prev = previousTopic();
        return v('div', { key: `${keyval}-prev-next-block`, classes: [pageCss.nextprev] }, [
            prev.path !== null
                ? v('div', {
                    tabIndex: 0,
                    key: 'prev-topic',
                    xqa: 'action prev',
                    role: 'button',
                    'aria-label': `Previous: ${prev.title}`,
                    classes: [pageCss.previous],
                    onclick: (event) => {
                        event && event.stopPropagation();
                        icache.delete('topic');
                        const { view } = pathmap.get(prev.path);
                        view();
                    },
                    onkeydown: (event) => {
                        const { view } = pathmap.get(prev.path);
                        switch (event.key) {
                            case Keys.Enter:
                            case Keys.Space:
                            case Keys.Spacebar:
                                event.preventDefault();
                                icache.delete('topic');
                                view();
                        }
                    }
                }, [
                    w(Icon, { faname: 'angle-left', size: 'sm' }),
                    v('span', { 'aria-hidden': 'true' }, [prev.title])
                ])
                : v('div', { key: 'prev-topic', 'aria-hidden': 'true', classes: [pageCss.previous] }),
            v('div', {
                tabIndex: 0,
                key: 'next-topic',
                xqa: 'action next',
                role: 'button',
                'aria-label': `Next: ${next.title}`,
                classes: [pageCss.next],
                onclick: (event) => {
                    event && event.stopPropagation();
                    next.position && readingTopicQueueChange({ key: parent, data: { cursor: next.position } });
                    isUsingQueue &&
                        !next.position &&
                        readingTopicQueueChange({ key: parent, data: { complete: true, cursor: 0 } });
                    icache.delete('topic');
                    const { view } = pathmap.get(next.path);
                    window.scrollTo(0, 0);
                    view();
                },
                onkeydown: (event) => {
                    const { view } = pathmap.get(next.path);
                    switch (event.key) {
                        case Keys.Enter:
                        case Keys.Space:
                        case Keys.Spacebar:
                            event.preventDefault();
                            next.position &&
                                readingTopicQueueChange({ key: parent, data: { cursor: next.position } });
                            isUsingQueue &&
                                !next.position &&
                                readingTopicQueueChange({ key: parent, data: { complete: true, cursor: 0 } });
                            icache.delete('topic');
                            window.scrollTo(0, 0);
                            view();
                    }
                }
            }, [
                v('span', { 'aria-hidden': 'true' }, [next.title]),
                w(Icon, { faname: icache.get('lastTopic') ? 'angle-double-right' : 'angle-right', size: 'sm' })
            ])
        ]);
    };
    const playlist = [];
    if (has('use-audio-topics')) {
        playlist.push({ key: rel, title });
    }
    const prevTopic = icache.get('prevTopic');
    if (prevTopic !== rel) {
        icache.set('prevTopic', rel);
        audio.teardown();
        audio.setup(playlist);
    }
    const audioWidget = has('use-audio-topics') && playlist ? w(AudioPlayer, { key: `audio-player-${rel}`, audio }) : null;
    load_topic(rel, bookversion, lang).then((topic) => {
        icache.set('topic', topic, true);
    });
    const topic = icache.getOrSet('topic', async () => {
        const topic = (await load_topic(rel, bookversion, lang));
        return topic;
    });
    const topic_content = topic
        ? [
            v('div', {
                key: `${topic.topic.attributes.id}`,
                xqa: `topic ${topic.topic.attributes.id}`,
                classes: [themeCss.topicBlock]
            }, [
                w('Generator', {
                    key: `${topic.topic.attributes.id}-body`,
                    skipTitle: false,
                    renderBody: true,
                    topic: topic
                })
            ])
        ]
        : [v('div', { key: `${rel}-temp`, xqa: `temp ${rel}` })];
    if (topic) {
        const attributes = { key: rel, title, rel };
        const allTopics = [{ topic: { attributes, children: [] } }];
        icache.getOrSet('loaded', async () => {
            await handleReadingLoaded(allTopics);
            return true;
        });
        const lastElement = document.getElementById('topicgroup-actions');
        if (lastElement && !icache.get('observing')) {
            icache.set('observing', true);
            const readingObserver = new IntersectionObserver(async (entries, observer) => {
                if (icache.get('processingCompleted')) {
                    return;
                }
                if (entries[0].isIntersecting) {
                    icache.set('processingCompleted', true);
                    await handleReadingCompleted(allTopics);
                    observer.unobserve(entries[0].target);
                }
            }, { threshold: 1 });
            readingObserver.observe(lastElement);
        }
    }
    const assoc = association ?? get(path('title')).toLocaleUpperCase();
    const product = get(path('product'));
    document.title = `${reading} - ${title} (${assoc} ${product.toLocaleUpperCase()} )`;
    return v('div', { key: `${rel}`, xqa: `topic ${rel}`, classes: [themeCss.root] }, [
        v('div', { key: `${rel}-title`, classes: [themeCss.titleBlock] }, [
            v('h1', { classes: [themeCss.title] }, [title])
        ]),
        next_prev('top'),
        v('div', {
            classes: [themeCss.group]
        }, [audioWidget, ...topic_content]),
        next_prev('bottom'),
        hasRelatedTopics && Boolean(parseInt(hasRelatedTopics)) ? w(RelatedTopics, { topicid: rel }) : null
    ]);
});
